import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { MaskLines, FadeUp, Reveal } from "@/components/Motion";
import { BrandMarquee } from "@/components/BrandMarquee";
import founderImg from "@/assets/founder.png";

const IMG = {
  gold: "https://images.unsplash.com/photo-1714515982656-3f8b7bfba88a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHxnb2xkJTIwbWluaW1hbCUyMGFic3RyYWN0JTIwZmluYW5jZXxlbnwwfHx8fDE3ODUyNTEyNDR8MA&ixlib=rb-4.1.0&q=85",
  office: "https://images.unsplash.com/photo-1706074793638-da28b90ea8ae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg1MjUxMjQ0fDA&ixlib=rb-4.1.0&q=85",
  founder: "https://images.unsplash.com/photo-1604904612715-47bf9d9bc670?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwzfHxlbGVnYW50JTIwYnVzaW5lc3MlMjB3b21hbiUyMHBvcnRyYWl0JTIwb2ZmaWNlfGVufDB8fHx8MTc4NTI1MTI0NHww&ixlib=rb-4.1.0&q=85",
};

const Home = () => {
  const { t } = useLang();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 160]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-end overflow-hidden bg-ivory pt-24">
        <motion.div style={{ y, scale }} className="absolute right-0 top-0 h-full w-full lg:w-[52%] pointer-events-none">
          <div className="absolute inset-0 bg-navy/5" />
          <img src={IMG.gold} alt="" className="w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-r from-ivory via-ivory/40 to-transparent lg:from-ivory/70" />
        </motion.div>

        <div className="relative z-10 max-w-[1500px] mx-auto px-6 lg:px-12 w-full pb-20 lg:pb-28">
          <FadeUp delay={0.15}>
            <span className="inline-block text-[12px] uppercase tracking-[0.3em] text-gold font-semibold mb-8">
              {t.hero.eyebrow}
            </span>
          </FadeUp>

          <h1 className="hero-title font-serif text-navy font-semibold">
            <MaskLines lines={t.hero.titleLines} lineClassName="text-navy" />
            <span className="line-mask">
              <motion.span
                className="block text-gold italic"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1], delay: 0.1 + t.hero.titleLines.length * 0.12 }}
              >
                {t.hero.titleAccent}
              </motion.span>
            </span>
          </h1>

          <FadeUp delay={0.9} className="mt-10 max-w-xl">
            <p className="text-base lg:text-lg text-navy/70 leading-relaxed">{t.hero.lead}</p>
          </FadeUp>

          <FadeUp delay={1.05} className="mt-12 flex flex-wrap items-center gap-5">
            <Link to="/contact" data-testid="hero-cta" className="btn-primary group inline-flex items-center gap-3 px-8 py-4 text-[13px] uppercase tracking-[0.18em] font-semibold">
              {t.hero.cta}
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link to="/services" data-testid="hero-cta-secondary" className="nav-link text-[13px] uppercase tracking-[0.18em] font-semibold text-navy">
              {t.hero.ctaSecondary}
            </Link>
          </FadeUp>
        </div>

        <motion.div style={{ opacity }} className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:block">
          <div className="w-[1px] h-14 bg-gradient-to-b from-gold to-transparent animate-pulse" />
        </motion.div>
      </section>

      <BrandMarquee items={t.marquee} />

      {/* PILLARS */}
      <section className="py-24 lg:py-32 bg-ivory" data-testid="pillars-section">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
          <Reveal>
            <span className="text-[12px] uppercase tracking-[0.3em] text-gold font-semibold">{t.pillars.eyebrow}</span>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-3 gap-px bg-navy/10 border border-navy/10">
            {t.pillars.items.map((p, i) => (
              <Reveal key={i} delay={0.1 * i}>
                <div className="bg-ivory p-8 lg:p-12 h-full">
                  <span className="font-serif text-5xl text-gold/50">{p.num}</span>
                  <h3 className="mt-8 font-serif text-3xl text-navy">{p.title}</h3>
                  <p className="mt-4 text-navy/70 leading-relaxed">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>


      {/* INTRO */}
      <section className="py-24 lg:py-36 bg-ivory" data-testid="intro-section">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <Reveal>
              <span className="text-[12px] uppercase tracking-[0.3em] text-gold font-semibold">{t.intro.eyebrow}</span>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal delay={0.1}>
              <h2 className="font-serif text-4xl lg:text-6xl text-navy leading-[1.05] max-w-3xl">{t.intro.title}</h2>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-navy/70 text-lg leading-relaxed max-w-2xl">{t.intro.body}</p>
            </Reveal>
            <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-8 lg:gap-4">
              {t.intro.stats.map((s, i) => (
                <Reveal key={i} delay={0.1 * i}>
                  <div className="border-t border-gold/40 pt-5">
                    <div className="font-serif text-5xl lg:text-6xl text-navy">{s.value}</div>
                    <div className="mt-2 text-[12px] uppercase tracking-[0.15em] text-navy/60">{s.label}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24 lg:py-36 bg-navy text-ivory relative overflow-hidden grain" data-testid="services-preview">
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <div>
              <Reveal><span className="text-[12px] uppercase tracking-[0.3em] text-gold font-semibold">{t.servicesPreview.eyebrow}</span></Reveal>
              <Reveal delay={0.1}><h2 className="mt-6 font-serif text-4xl lg:text-6xl leading-[1.05] max-w-2xl">{t.servicesPreview.title}</h2></Reveal>
            </div>
            <Reveal delay={0.2}>
              <Link to="/services" data-testid="services-preview-cta" className="group inline-flex items-center gap-3 text-[13px] uppercase tracking-[0.18em] font-semibold text-gold">
                {t.servicesPreview.cta}
                <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-gold/20 border border-gold/20">
            {t.services.map((s, i) => (
              <Reveal key={i} delay={0.1 * i}>
                <Link to="/services" className="group block bg-navy hover:bg-navy-deep transition-colors duration-500 p-8 lg:p-10 h-full">
                  <div className="flex items-start justify-between">
                    <span className="font-serif text-6xl text-gold/40 group-hover:text-gold transition-colors duration-500">{s.num}</span>
                    <ArrowUpRight size={22} className="text-ivory/40 group-hover:text-gold transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </div>
                  <h3 className="mt-10 font-serif text-3xl">{s.title}</h3>
                  <p className="mt-4 text-ivory/60 text-sm leading-relaxed">{s.short}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="py-24 lg:py-36 bg-ivory" data-testid="manifesto-section">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5 lg:sticky lg:top-28 self-start">
              <Reveal><span className="text-[12px] uppercase tracking-[0.3em] text-gold font-semibold">{t.manifesto.eyebrow}</span></Reveal>
              <Reveal delay={0.1}><h2 className="mt-6 font-serif text-4xl lg:text-6xl text-navy leading-[1.05]">{t.manifesto.title}</h2></Reveal>
              <Reveal delay={0.2}>
                <div className="mt-10 relative clip-editorial overflow-hidden">
                  <img src={IMG.office} alt="Office" className="w-full h-72 object-cover" />
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-7 lg:col-start-7">
              {t.manifesto.chapters.map((c, i) => (
                <Reveal key={i} delay={0.05 * i}>
                  <div className="py-10 border-b border-navy/10 flex gap-8">
                    <span className="font-serif text-5xl lg:text-7xl text-gold/60 leading-none">{c.num}</span>
                    <div>
                      <h3 className="font-serif text-3xl text-navy">{c.title}</h3>
                      <p className="mt-3 text-navy/70 leading-relaxed">{c.body}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER TEASER */}
      <section className="py-24 lg:py-36 bg-navy text-ivory relative overflow-hidden grain" data-testid="founder-teaser">
        <div className="relative max-w-[1500px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="clip-editorial overflow-hidden max-w-md">
                <img src={founderImg} alt={t.founder.name} className="w-full h-[520px] object-cover object-top" data-testid="founder-image" />
              </div>
              <div className="absolute -bottom-4 -right-2 lg:right-16 bg-gold text-navy px-6 py-4">
                <div className="font-serif text-xl leading-none">{t.founder.name}</div>
                <div className="text-[11px] uppercase tracking-[0.12em] mt-1">{t.founder.role}</div>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal><span className="text-[12px] uppercase tracking-[0.3em] text-gold font-semibold">{t.founder.eyebrow}</span></Reveal>
            <Reveal delay={0.1}>
              <blockquote className="mt-8 font-serif text-3xl lg:text-4xl italic leading-snug text-ivory">
                “{t.founder.quote}”
              </blockquote>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-8 text-ivory/70 leading-relaxed max-w-lg">{t.founder.body}</p>
            </Reveal>
            <Reveal delay={0.3}>
              <Link to="/cabinet" data-testid="founder-cta" className="mt-10 inline-flex btn-outline-gold px-8 py-4 text-[13px] uppercase tracking-[0.18em] font-semibold text-ivory border-gold hover:text-navy">
                {t.nav.about}
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      <CtaBand />
    </div>
  );
};

export const CtaBand = () => {
  const { t } = useLang();
  return (
    <section className="py-24 lg:py-32 bg-ivory" data-testid="cta-band">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-12">
        <div className="border border-gold/40 px-8 lg:px-20 py-16 lg:py-24 text-center relative">
          {t.ctaBand.note && (
            <Reveal>
              <span className="inline-block mb-6 text-[11px] uppercase tracking-[0.22em] text-gold font-semibold border border-gold/40 px-4 py-2">
                {t.ctaBand.note}
              </span>
            </Reveal>
          )}
          <Reveal><h2 className="font-serif text-4xl lg:text-6xl text-navy leading-[1.05] max-w-3xl mx-auto">{t.ctaBand.title}</h2></Reveal>
          <Reveal delay={0.1}><p className="mt-6 text-navy/70 text-lg max-w-xl mx-auto">{t.ctaBand.body}</p></Reveal>
          <Reveal delay={0.2}>
            <Link to="/contact" data-testid="ctaband-btn" className="btn-primary group inline-flex items-center gap-3 mt-10 px-10 py-5 text-[13px] uppercase tracking-[0.18em] font-semibold">
              {t.ctaBand.cta}
              <ArrowUpRight size={18} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Home;
